// #region Imports
import { defineComponent } from "vue";
// #endregion
// #region Konstanten
const delta = 2.5;
// #endregion
export default defineComponent({
    // #region Vars
    name: 'v-horizontal',
    // #endregion
    // #region Properties
    data() {
        return {
            left: 0,
            width: 0,
            scrollWidth: 0,
            hasPrev: false,
            hasNext: false,
            debounceId: undefined,
            totalElements: 0,
        };
    },
    props: {
        /**
         * Navigation button visibility
         */
        button: {
            type: Boolean,
            default: () => true,
        },
        /**
         * Navigation button alignment, default to between the edge of the horizontal axis.
         */
        buttonBetween: {
            type: Boolean,
            default: () => true
        },
        /**
         * Scrollbar visibility
         */
        scroll: {
            type: Boolean,
            default: () => false,
        },
        /**
         * Use default responsive breakpoint.
         */
        responsive: {
            type: Boolean,
            default: () => false,
        },
        /**
         * Move window, indicates the percent of width to travel when nav is triggered.
         */
        displacement: {
            type: Number,
            default: () => 1.0
        },
        /**
         * Snap to start|center|end
         */
        snap: {
            type: String,
            default: () => 'start',
        },
        showIndicators: {
            type: Boolean,
            default: () => false,
        },
        containerClass: {
            type: String,
            required: false,
            default: () => '',
        },
    },
    // #endregion
    // #region Constructor
    mounted() {
        this.onScrollDebounce();
    },
    beforeDestroy() {
        clearTimeout(this.debounceId);
    },
    // #endregion
    // #region Core
    computed: {
        currentIndex() {
            return Math.round(this.left / this.width);
        },
    },
    methods: {
        _children() {
            const container = this.$refs.container;
            return container?.children;
        },
        children() {
            const container = this.$refs.container;
            return container?.children;
        },
        findPrevSlot(x) {
            const children = this.children();
            for (let i = 0; i < children.length; i++) {
                const rect = children[i].getBoundingClientRect();
                if (rect.left <= x && x <= rect.right) {
                    return children[i];
                }
                if (x <= rect.left) {
                    return children[i];
                }
            }
        },
        findNextSlot(x) {
            const children = this.children();
            for (let i = 0; i < children.length; i++) {
                const rect = children[i].getBoundingClientRect();
                if (rect.right <= x) {
                    continue;
                }
                else if (rect.left <= x) {
                    return children[i];
                }
                if (x <= rect.left) {
                    return children[i];
                }
            }
        },
        /**
         * Toggle and scroll to the previous set of horizontal content.
         */
        prev() {
            this.$emit('prev');
            const container = this.$refs.container;
            const left = container.getBoundingClientRect().left;
            const x = left + (container.clientWidth * -this.displacement) - delta;
            const element = this.findPrevSlot(x);
            if (element) {
                const width = element.getBoundingClientRect().left - left;
                this.scrollToLeft(container.scrollLeft + width);
                return;
            }
            const width = container.clientWidth * this.displacement;
            this.scrollToLeft(container.scrollLeft - width);
        },
        /**
         * Toggle and scroll to the next set of horizontal content.
         */
        next() {
            this.$emit('next');
            const container = this.$refs.container;
            const left = container.getBoundingClientRect().left;
            const x = left + (container.clientWidth * this.displacement) + delta;
            const element = this.findNextSlot(x);
            if (element) {
                const width = element.getBoundingClientRect().left - left;
                if (width > delta) {
                    this.scrollToLeft(container.scrollLeft + width);
                    return;
                }
            }
            const width = container.clientWidth * this.displacement;
            this.scrollToLeft(container.scrollLeft + width);
        },
        /**
         * Index of the slots to scroll to.
         * @param i index
         */
        scrollToIndex(i, behavior = "smooth") {
            const children = this.children();
            if (children[i]) {
                const container = this.$refs.container;
                const rect = children[i].getBoundingClientRect();
                const left = rect.left - container.getBoundingClientRect().left;
                this.scrollToLeft(container.scrollLeft + left, behavior);
            }
        },
        /**
         * Amount of pixel to scroll to on the left.
         * @param left of the horizontal
         * @param behavior smooth|auto
         */
        scrollToLeft(left, behavior = "smooth") {
            const element = this.$refs.container;
            element.scrollTo({ left: left, behavior: behavior });
        },
        onScroll() {
            const container = this.$refs.container;
            // Resolves https://github.com/fuxingloh/vue-horizontal/issues/99#issue-862691647
            if (!container)
                return;
            this.$emit('scroll', {
                left: container.scrollLeft,
            });
            clearTimeout(this.debounceId);
            // @ts-ignore
            this.debounceId = setTimeout(this.onScrollDebounce, 100);
        },
        onScrollDebounce() {
            this.refresh((data) => {
                this.$emit('scroll-debounce', data);
            });
        },
        reload() {
            this.refresh((data) => {
                this.$emit('scroll-debounce', data);
            });
        },
        /**
         * Manually refresh vue-horizontal
         * @param callback after refreshed, optional
         */
        refresh(callback) {
            this.$nextTick(() => {
                const data = this.calculate();
                this.left = data.left;
                this.width = data.width;
                this.scrollWidth = data.scrollWidth;
                this.hasNext = data.hasNext;
                this.hasPrev = data.hasPrev;
                this.totalElements = data.totalElements;
                callback(data);
            });
        },
        calculate() {
            const container = this.$refs.container;
            const firstChild = this.children()[0];
            function hasNext() {
                return container.scrollWidth > container.scrollLeft + container.clientWidth + delta;
            }
            function hasPrev() {
                if (container.scrollLeft === 0) {
                    return false;
                }
                const containerVWLeft = container.getBoundingClientRect().left;
                const firstChildLeft = firstChild?.getBoundingClientRect()?.left ?? 0;
                return Math.abs(containerVWLeft - firstChildLeft) >= delta;
            }
            return {
                left: container.scrollLeft,
                width: container.clientWidth,
                scrollWidth: container.scrollWidth,
                hasNext: hasNext(),
                hasPrev: hasPrev(),
                totalElements: this.children().length,
            };
        },
        onIndexClick(i) {
            this.scrollToIndex(i);
        },
    },
    // #endregion
});
